import React, { useState, useEffect } from 'react';
import './App.css';
import './App-mobile.css';
import VoiceRecorder from './components/VoiceRecorder';
import VoiceStats from './components/VoiceStats';
import PracticeSentenceFeedback from './components/PracticeSentenceFeedback';
import PerformanceDebugPanel from './components/PerformanceDebugPanel';
import ModeSelector, { AppMode } from './components/ModeSelector';
import CoachMode from './components/CoachMode';
import AdminDashboard from './components/AdminDashboard';
import AdminDashboardEnhanced from './components/AdminDashboardEnhanced';
import HindiPracticeMode from './components/HindiPracticeMode';
import PracticeMode, { PracticeSentence } from './components/PracticeMode';
import AuthButtons from './components/AuthButtons';
import { AudioAnalyzer, AudioStats } from './utils/audioAnalysis';
import { PerformanceLogger } from './utils/performanceLogger';
import { useAuth, useIsAdmin, useIsCoach } from './contexts/AuthContext';

function App() {
  const { user, loading } = useAuth();
  const isAdmin = useIsAdmin();
  const isCoach = useIsCoach();
  
  const [audioStats, setAudioStats] = useState<AudioStats | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null); // Store blob for re-analysis
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const [quickMode, setQuickMode] = useState(false); // Default to DETAILED mode (was true for FAST mode)
  const [wasQuickAnalysis, setWasQuickAnalysis] = useState(false); // Track if current results are from quick mode
  const [debugTranscript, setDebugTranscript] = useState<any>(null); // Store transcript for debug display
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [storedTranscript, setStoredTranscript] = useState<any>(null); // Store transcript for re-analysis
  const [practiceMode, setPracticeMode] = useState<'free' | 'guided' | null>(null);
  const [selectedSentence, setSelectedSentence] = useState<PracticeSentence | null>(null);
  const [learnerLanguage, setLearnerLanguage] = useState<'english' | 'hindi' | null>(null);
  
  // Check URL parameters for initial mode
  const getInitialMode = (): AppMode | null => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    if (mode === 'admin' || mode === 'coach' || mode === 'learner') {
      return mode as AppMode;
    }
    return null;
  };
  
  const [appMode, setAppMode] = useState<AppMode | null>(getInitialMode());
  const [hasInitialized, setHasInitialized] = useState(false);

  // Auto-select learner mode and Hindi for non-admin users
  useEffect(() => {
    // Wait for auth to load
    if (loading) return;

    // Only initialize once
    if (hasInitialized) return;

    // For non-admin users, auto-select learner mode and Hindi
    if (!isAdmin && !appMode) {
      setAppMode('learner');
      setLearnerLanguage('hindi');
      setHasInitialized(true);
    }
    // For admin users, just mark as initialized
    else if (isAdmin) {
      setHasInitialized(true);
    }
    // Auto-select Hindi for non-admin learners
    else if (appMode === 'learner' && !learnerLanguage && !isAdmin) {
      setLearnerLanguage('hindi');
      setHasInitialized(true);
    }
  }, [appMode, learnerLanguage, isAdmin, loading, hasInitialized]);

  const handleRecordingComplete = async (audioBlob: Blob, url: string, transcript?: any) => {
    const perfLogger = PerformanceLogger.getInstance();
    const recordingDuration = audioBlob.size / (16000 * 2); // Estimate based on typical audio encoding
    
    console.log('App.tsx - handleRecordingComplete called');
    console.log('App.tsx - Received transcript:', transcript);
    console.log('App.tsx - Transcript type:', typeof transcript);
    console.log('App.tsx - Transcript properties:', transcript ? Object.keys(transcript) : 'null/undefined');
    if (transcript) {
      console.log('App.tsx - Transcript text:', transcript.transcript);
      console.log('App.tsx - Transcript isFinal:', transcript.isFinal);
      console.log('App.tsx - Transcript words:', transcript.words);
    }
    
    // Store for debug display and re-analysis
    setDebugTranscript(transcript);
    setStoredTranscript(transcript); // Save for potential re-analysis
    
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    setError('');
    setAudioUrl(url);
    setAudioBlob(audioBlob); // Store blob for potential re-analysis
    
    // Start total analysis timing
    perfLogger.startTiming('totalAnalysis');
    
    try {
      const analyzer = new AudioAnalyzer();
      
      // Listen for progress updates from Web Worker
      if ((analyzer as any).worker) {
        (analyzer as any).worker.addEventListener('message', (e: MessageEvent) => {
          if (e.data.type === 'ANALYSIS_PROGRESS') {
            setAnalysisProgress(e.data.progress);
          }
        });
      }
      
      // Pass quickMode flag - when true, skips phoneme analysis
      console.log('App.tsx - About to call analyzeAudio with transcript:', transcript);
      const stats = await analyzer.analyzeAudio(audioBlob, !quickMode, transcript);
      console.log('App.tsx - Received stats with transcription:', stats.transcription);
      setAudioStats(stats);
      setWasQuickAnalysis(quickMode); // Track analysis mode
      
      // End timing and log performance
      perfLogger.endTiming('totalAnalysis');
      await perfLogger.logPerformance(stats.duration, false, undefined, quickMode ? 'quick' : 'detailed');
      
    } catch (err) {
      console.error('Error analyzing audio:', err);
      setError('Failed to analyze audio. Please try again.');
      
      // Log error
      perfLogger.endTiming('totalAnalysis');
      await perfLogger.logPerformance(
        recordingDuration,
        true,
        err instanceof Error ? err.message : 'Unknown error',
        quickMode ? 'quick' : 'detailed'
      );
    } finally {
      setIsAnalyzing(false);
      setAnalysisProgress(0);
    }
  };

  const handleReset = () => {
    setAudioStats(null);
    setAudioUrl('');
    setAudioBlob(null);
    setAnalysisProgress(0);
    setError('');
    setWasQuickAnalysis(false);
    setStoredTranscript(null);
    setDebugTranscript(null);
    // Don't clear practice mode and selected sentence - keep them for next recording
    // setPracticeMode(null);
    // setSelectedSentence(null);
  };
  
  const handlePracticeModeSelect = (mode: 'free' | 'guided', sentence?: PracticeSentence) => {
    setPracticeMode(mode);
    if (sentence) {
      setSelectedSentence(sentence);
    }
  };
  
  const handleSentenceReset = () => {
    // Clear everything when going back to sentence selection
    setAudioStats(null);
    setAudioUrl('');
    setAudioBlob(null);
    setPracticeMode(null);
    setSelectedSentence(null);
  };

  const handleDetailedReanalysis = async () => {
    if (!audioBlob) return;
    
    const perfLogger = PerformanceLogger.getInstance();
    
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    setError('');
    
    // Start total analysis timing
    perfLogger.startTiming('totalAnalysis');
    
    try {
      const analyzer = new AudioAnalyzer();
      
      // Listen for progress updates from Web Worker
      if ((analyzer as any).worker) {
        (analyzer as any).worker.addEventListener('message', (e: MessageEvent) => {
          if (e.data.type === 'ANALYSIS_PROGRESS') {
            setAnalysisProgress(e.data.progress);
          }
        });
      }
      
      // Perform detailed analysis (with phoneme analysis enabled)
      console.log('Re-analysis: Passing stored transcript:', storedTranscript);
      const stats = await analyzer.analyzeAudio(audioBlob, true, storedTranscript);
      console.log('Re-analysis: Received stats with transcription:', stats.transcription);
      setAudioStats(stats);
      setWasQuickAnalysis(false); // Now we have detailed results
      
      // End timing and log performance
      perfLogger.endTiming('totalAnalysis');
      await perfLogger.logPerformance(stats.duration, false, undefined, 're-analysis');
      
    } catch (err) {
      console.error('Error analyzing audio:', err);
      setError('Failed to analyze audio. Please try again.');
      
      // Log error
      perfLogger.endTiming('totalAnalysis');
      await perfLogger.logPerformance(
        audioBlob.size / (16000 * 2),
        true,
        err instanceof Error ? err.message : 'Unknown error',
        're-analysis'
      );
    } finally {
      setIsAnalyzing(false);
      setAnalysisProgress(0);
    }
  };

  // Show loading state while auth is loading
  if (loading) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>Speech Coach</h1>
          <p className="subtitle">Loading...</p>
        </header>
      </div>
    );
  }

  // Only show mode selector for admin users who haven't selected a mode
  if (!appMode && isAdmin) {
    return (
      <div className="App">
        <header className="App-header">
          <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
            <AuthButtons />
          </div>
          <h1>Speech Coach</h1>
          <p className="subtitle">Your personal speech and accent coach</p>
        </header>

        <main className="App-main">
          <ModeSelector
            currentMode={null}
            onModeChange={(mode) => {
              setAppMode(mode);
              // Reset state when switching modes
              setLearnerLanguage(null);
              setPracticeMode(null);
              setAudioStats(null);
              setAudioUrl('');
              setAudioBlob(null);
            }}
            isAdmin={isAdmin}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header compact">
        <div style={{ position: 'absolute', top: '0.5rem', right: '1rem' }}>
          <AuthButtons />
        </div>
        {isAdmin && (
          <div className="mode-switch">
            <button
              className="mode-switch-btn"
              onClick={() => {
                setAppMode(null);  // Go back to mode selection
                setLearnerLanguage(null);  // Reset language selection
                setPracticeMode(null);  // Reset practice mode
                setAudioStats(null);  // Reset any analysis
                setAudioUrl('');
                setAudioBlob(null);
              }}
            >
              ← Change Mode (Current: {appMode === 'learner' ? 'Learner' : appMode === 'coach' ? 'Coach' : 'Admin'})
            </button>
          </div>
        )}
        <div className="header-center">
          <h1>Speech Coach</h1>
          <p className="subtitle">Your personal speech and accent coach</p>
        </div>
        <div className="header-nav">
          {/* Only show language change button for admins */}
          {appMode === 'learner' && learnerLanguage && isAdmin && (
            <button 
              className="mode-switch-btn"
              onClick={() => {
                setLearnerLanguage(null);
                setPracticeMode(null);
                setAudioStats(null);
                setAudioUrl('');
                setAudioBlob(null);
              }}
            >
              ← Change Language (Current: {learnerLanguage === 'english' ? 'English' : 'Hindi'})
            </button>
          )}
          {appMode === 'learner' && learnerLanguage === 'english' && practiceMode && (
            <button 
              className="mode-switch-btn"
              onClick={() => {
                setPracticeMode(null);
                setSelectedSentence(null);
                setAudioStats(null);
                setAudioUrl('');
                setAudioBlob(null);
              }}
            >
              ← Change Practice (Current: {practiceMode === 'free' ? 'Free Speech' : 'Guided'})
            </button>
          )}
        </div>
      </header>
      
      <main className="App-main">
        {appMode === 'coach' && (
          isCoach || isAdmin ? (
            <CoachMode />
          ) : (
            <div className="auth-required">
              <h2>Coach Access Required</h2>
              <p>You need coach permissions to access this mode.</p>
              <p>Please sign in with an authorized account or contact an administrator.</p>
            </div>
          )
        )}
        
        {appMode === 'admin' && (
          isAdmin ? (
            <AdminDashboardEnhanced />
          ) : (
            <div className="auth-required">
              <h2>Admin Access Required</h2>
              <p>You need administrator permissions to access this mode.</p>
              <p>Please sign in with an authorized account.</p>
            </div>
          )
        )}
        
        {/* For admins, show language selection. For regular learners, auto-select Hindi */}
        {appMode === 'learner' && !learnerLanguage && isAdmin && (
          <div className="welcome-section">
            <h2>Choose Your Practice Language</h2>
            <div className="language-selection-cards">
              <div className="language-card">
                <div className="language-card-inner" onClick={() => setLearnerLanguage('hindi')}>
                  <div className="language-flag">🇮🇳</div>
                  <h3>Hindi</h3>
                  <p>Learn Hindi pronunciation with guided sentences and native speaker reference</p>
                </div>
              </div>
              
              <div className="language-card">
                <div className="language-card-inner" onClick={() => setLearnerLanguage('english')}>
                  <div className="language-flag">🇬🇧</div>
                  <h3>English</h3>
                  <p>Practice English pronunciation with tongue twisters and free speech</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        
        {appMode === 'learner' && learnerLanguage === 'hindi' && <HindiPracticeMode />}
        
        {appMode === 'learner' && learnerLanguage === 'english' && !audioStats && !isAnalyzing && (
          <div className="welcome-section">
            {!practiceMode ? (
              <>
                <h2>Choose Your Practice Method</h2>
                <PracticeMode onModeSelect={handlePracticeModeSelect} />
              </>
            ) : practiceMode === 'guided' && !selectedSentence ? (
              <>
                <PracticeMode onModeSelect={handlePracticeModeSelect} />
              </>
            ) : (
              <>
                <h2>Record Your Voice</h2>
                {practiceMode === 'free' && (
                  <p>Speak naturally about any topic for at least 10 seconds. The system will analyze your pronunciation.</p>
                )}
                
                {/* Analysis mode toggle hidden but kept for future use */}
                <div className="analysis-mode-toggle" style={{ display: 'none' }}>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={!quickMode}
                      onChange={(e) => setQuickMode(!e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="mode-text">
                      {quickMode ? '⚡ Quick Analysis (< 2 seconds)' : '🔬 Detailed Analysis (8-15 seconds)'}
                    </span>
                  </label>
                  <p className="mode-description">
                    {quickMode 
                      ? 'Fast mode provides instant results with basic metrics'
                      : 'Detailed mode includes advanced phoneme analysis and recommendations (slower)'}
                  </p>
                </div>
                
                <VoiceRecorder 
                  onRecordingComplete={handleRecordingComplete}
                  targetSentence={selectedSentence?.text}
                  onReset={handleSentenceReset}
                />
              </>
            )}
            
            {/* Debug Panel for App-level transcript */}
            {window.location.search.includes('debug=true') && debugTranscript && (
              <div style={{
                position: 'fixed',
                top: '10px',
                left: '10px',
                background: '#ffe6e6',
                border: '2px solid #cc0000',
                borderRadius: '5px',
                fontFamily: 'monospace',
                fontSize: '12px',
                zIndex: 1000,
                maxWidth: '400px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
              }}>
                <div style={{
                  padding: '10px',
                  background: '#cc0000',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }} onClick={() => setDebugPanelOpen(!debugPanelOpen)}>
                  <span>🔍 App: Received Transcript</span>
                  <span>{debugPanelOpen ? '▼' : '▶'}</span>
                </div>
                {debugPanelOpen && (
                  <div style={{padding: '10px'}}>
                    <div>Transcript Text: {debugTranscript?.transcript?.substring(0, 200) || 'None'}</div>
                    <div>Is Final: {debugTranscript?.isFinal ? '✅ YES' : '❌ NO'}</div>
                    <div>Words: {JSON.stringify(debugTranscript?.words?.slice(0, 10))}</div>
                    <div>Confidence: {debugTranscript?.confidence}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {appMode === 'learner' && learnerLanguage === 'english' && isAnalyzing && (
          <div className="analyzing-section">
            <div className="spinner"></div>
            <h2>Analyzing your voice...</h2>
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${analysisProgress}%` }}
                />
              </div>
              <span className="progress-text">{Math.round(analysisProgress)}%</span>
            </div>
            <p>Please wait while we process your recording with advanced phoneme analysis.</p>
          </div>
        )}
        
        {appMode === 'learner' && learnerLanguage === 'english' && audioStats && !isAnalyzing && (
          <div className="results-section">
            {(() => {
              console.log('[App] Results section render:', {
                appMode,
                hasAudioStats: !!audioStats,
                isAnalyzing,
                practiceMode,
                hasSelectedSentence: !!selectedSentence,
                selectedSentenceText: selectedSentence?.text
              });
              return null;
            })()}
            {practiceMode === 'guided' && selectedSentence ? (
              <PracticeSentenceFeedback 
                stats={audioStats} 
                targetSentence={selectedSentence.text}
                audioUrl={audioUrl} 
              />
            ) : (
              <VoiceStats stats={audioStats} audioUrl={audioUrl} />
            )}
            {wasQuickAnalysis && audioBlob && (
              <div className="reanalysis-section">
                <p className="reanalysis-prompt">
                  Want more detailed insights? Run advanced phoneme analysis on your recording (takes 8-15 seconds).
                </p>
                <button className="detailed-analysis-btn" onClick={handleDetailedReanalysis}>
                  🔬 Get Detailed Analysis
                </button>
              </div>
            )}
            <button className="reset-btn" onClick={handleReset}>
              Record Another Sample
            </button>
          </div>
        )}
        
        {appMode === 'learner' && error && (
          <div className="error-message">
            <p>{error}</p>
            <button className="reset-btn" onClick={handleReset}>
              Try Again
            </button>
          </div>
        )}
      </main>
      
      <footer className="App-footer">
        <p>© 2025 Speech Coach. Helping you improve your speech and accent, one recording at a time.</p>
      </footer>
      
      <PerformanceDebugPanel />
    </div>
  );
}

export default App;